<template>
	<div class="ReportList">
		<div class="ReportList-heading">
			<CloHeading level="2">{{ currentViewTitle }}</CloHeading>
			<!-- Details button -->
			<button
				v-show="!isMediaView && !loadingData && currentView !== 'checklists' && speciesList.length !== 0"
				@click="isShowingAll = !isShowingAll"
				class="Button Button--link"
			>
				<template v-if="isShowingAll"> {{ $t('generic.hideAllDetails') }} </template>
				<template v-else> {{ $t('generic.showAllDetails') }} </template>
			</button>
			<!-- Manage checklists link -->
			<a
				v-if="currentView === 'checklists' && currentUser.isParticipant && checklists.length !== 0"
				:href="`${baseUrl}tripreport/edit/${tripReportId}?view=checklists`"
				class="Button Button--small Button--hollow u-margin-none"
				>{{ $t('tripReport.manageChecklists') }}</a
			>
		</div>
		<!-- -------- Native or naturalized species -------- -->
		<CloLoadingAnimation v-if="loadingData" class="LoadingAnimation LoadingAnimation--large LoadingAnimation--basic" />
		<div v-if="!loadingData && currentView !== 'checklists'" :style="{ '--maxCountWidth': maxCountWidth }">
			<!-- Loop through all list types (see data section below for list) -->
			<div v-for="listType in listTypes" :key="listType">
				<template v-if="listVisible(listType)">
					<div class="ReportList-subheading" :class="[{'is-visuallyHidden': listType === 'nativeNatList'}]">
						<CloHeading level="3" size="5" minor>{{ getListTitle(listType) }}</CloHeading>
					</div>
					<ul class="ReportList-list">
						<ReportListSpecies
							:tripReportId="tripReportId"
							:selectedPersonId="selectedPersonId"
							v-for="speciesListItem in getListArray(listType)"
							:speciesListItem="speciesListItem"
							:key="speciesListItem.speciesCode"
							:isMediaView="isMediaView"
							:isShowingAll="isShowingAll"
							:view="currentView"
							:liferFilters="liferFilters"
							:locale="locale"
							:sppPref="sppPref"
							:sppLocale="sppLocale"
							:currentUser="currentUser"
							:isPrintLayout="isPrintLayout"
						/>
					</ul>
				</template>
			</div>
		</div>
		<!-- -------- Checklists -------- -->
		<div v-if="!loadingData && currentView === 'checklists' && checklists.length !== 0">
			<ul class="ReportList-checklists">
				<li v-for="checklist in checklists" :key="checklist.subId">
					<ChecklistItem :checklist="checklist" :edit="false" />
				</li>
			</ul>
		</div>
		<!-- -------- Empty fallback -------- -->
		<!-- <div 
			v-if="!loadingData && ((currentView !== 'checklists' && speciesList.length === 0) || (currentView === 'checklists' && checklists.length === 0))" 
			class="u-inset-squish-responsive">
			<div class="Placeholder">
				<p>{{ $t('data.noChecklists') }}</p>
			</div>
		</div> -->
	</div>
</template>

<script>
import axios from 'axios';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation';
import ReportListSpecies from './ReportListSpecies.vue';
import ChecklistItem from './ChecklistItem.vue';

export default {
	name: 'ReportList',
	components: {
		CloButton,
		CloHeading,
		CloLoadingAnimation,
		ReportListSpecies,
		ChecklistItem,
	},
	data() {
		return {
			checklists: [],
			checklistsLoaded: false,
			speciesList: [],
			formList: [],
			hybridList: [],
			nativeNatList: [],
			provisionalsList: [],
			escapeesList: [],
			otherTaxaList: [],
			speciesLoaded: false,
			loadingData: true,
			isShowingAll: false,
			listTypes: ['nativeNatList', 'provisionalsList', 'escapeesList', 'hybridList', 'formList', 'otherTaxaList'],
			baseUrl: window.baseUrl,
			tripApiBaseUrl: window.tripApiBaseUrl,
		};
	},
	props: {
		tripReportId: {
			type: Number,
			required: true,
		},
		selectedPersonId: {
			type: String,
			default: '',
		},
		currentView: {
			type: String,
			default: '',
		},
		currentUser: Object,
		locale: String,
		liferFilters: {
			type: Array,
			default() {
				return [];
			},
		},
		isPrintLayout: Boolean,
		sppPref: {
			type: String,
			default: 'both',
		},
		sppLocale: {
			type: String,
			default: 'en',
		},
	},
	watch: {
		currentView: function (newVal, oldVal) {
			if (newVal === 'checklists' && !this.checklistsLoaded) {
				this.loadingData = true;
				this.getChecklists();
			} else if (!this.speciesLoaded) {
				this.loadingData = true;
				this.getSpeciesList();
			}
		},
	},
	computed: {
		maxCountWidth: function () {
			// Calculate the width needed to display the maxium count number in the list
			const counts = this.speciesList.map((species) => species.numIndividuals);
			const maxCount = Math.max(...counts);
			const maxCountDigits = maxCount.toString().length;
			return maxCountDigits + 'ch';
		},
		currentViewTitle: function () {
			switch (this.currentView) {
				case 'photos':
					return this.$t('data.speciesWithPhotos');
				case 'audio':
					return this.$t('data.speciesWithAudio');
				case 'checklists':
					return this.$t('generic.Checklists');
				default:
					return this.$t('data.speciesObserved');
			}
		},
		isMediaView: function () {
			return this.currentView === 'photos' || this.currentView === 'audio';
		},
	},
	mounted() {
		if (this.currentView === 'checklists') {
			this.getChecklists();
		} else {
			this.getSpeciesList();
		}
	},
	methods: {
		getChecklists() {
			let url = `${this.tripApiBaseUrl}checklists/${this.tripReportId}`;
			let params = {};
			if (this.selectedPersonId) {
				params.tripReportPersonId = this.selectedPersonId;
			}
			axios
				.get(url, { params: params })
				.then((res) => {
					// api returns null if no comments
					if (res.data) {
						this.checklists = res.data;
						this.checklistsLoaded = true;
						this.loadingData = false;
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		getSpeciesList() {
			let url = `${this.tripApiBaseUrl}taxon-list/${this.tripReportId}`;
			if (this.selectedPersonId) {
				url = url + '?tripReportPersonId=' + this.selectedPersonId;
			}
			axios
				.get(url)
				.then((res) => {
					// api returns null if no comments
					if (res.data) {
						// create filtered lists
						
						this.speciesList = res.data.filter((taxon) => taxon.category === 'species');
						this.hybridList = res.data.filter((taxon) => taxon.category === 'hybrid');
						this.formList = res.data.filter((taxon) => taxon.category === 'form');
						this.otherTaxaList = res.data.filter((taxon) => !['species', 'hybrid', 'form'].includes(taxon.category));

						this.provisionalsList = res.data.filter((taxon) => taxon.category === 'species' && taxon.exoticCategory === 'P');
						this.escapeesList = res.data.filter((taxon) => taxon.category === 'species' && taxon.exoticCategory === 'X');
						this.nativeNatList = res.data.filter((taxon) => taxon.category === 'species' && (!taxon.hasOwnProperty('exoticCategory') || taxon.exoticCategory === 'N'));

						this.speciesLoaded = true;
						this.loadingData = false;
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		listVisible(list) {
			// determine if sections should show when filters are set
			let isVisibleForCurrentFilter = true;
			if (this[list].length === 0) {
				// Hide when list is empty
				isVisibleForCurrentFilter = false;
			} else {
				// Establish filter presence
				const hasMediaFilter = (this.currentView === 'photos' || this.currentView === 'audio');
				const hasLiferFilter = (this.liferFilters.length);
				
				// When mediaFilter is active
				if (hasMediaFilter) {
					isVisibleForCurrentFilter = false;
					if (
						(this.currentView === 'photos' && this[list].some((x) => x.numPhotos)) ||
						(this.currentView === 'audio' && this[list].some((x) => x.numAudio))
					) {
						isVisibleForCurrentFilter = true;
					}
				}
				
				// When liferFilter is active (trumps media filter)
				if (hasLiferFilter) {
					isVisibleForCurrentFilter = false;
					if (
						(this.liferFilters.includes('s') && this[list].some((x) => x.isLifer)) ||
						(this.liferFilters.includes('p') && this[list].some((x) => x.isPhotoLifer)) ||
						(this.liferFilters.includes('a') && this[list].some((x) => x.isAudioLifer))
					) {
						isVisibleForCurrentFilter = true;
					}
				}
			}
			return isVisibleForCurrentFilter;
		},
		getListTitle(list) {
			// Return the list tile from the list id
			switch(list) {
				case 'nativeNatList':
					return this.$t('exotic.groupNativeNaturalized');
					break;
				case 'provisionalsList':
					return this.$t('exotic.categoryLabel', [this.$t('exotic.categoryP.title')])
					break;
				case 'escapeesList':
					return this.$t('exotic.categoryLabel', [this.$t('exotic.categoryX.title')])
					break;
				case 'hybridList':
					return this.$t('tripReport.hybrids')
					break;
				case 'formList':
					return this.$t('tripReport.forms')
					break;
				case 'otherTaxaList':
					return this.$t('tripReport.additionalTaxa')
					break;
				default:
					return ''
			}
		},
		getListArray(list) {
			switch(list) {
				case 'nativeNatList':
					return this.nativeNatList;
					break;
				case 'provisionalsList':
					return this.provisionalsList;
					break;
				case 'escapeesList':
					return this.escapeesList;
					break;
				case 'hybridList':
					return this.hybridList;
					break;
				case 'formList':
					return this.formList;
					break;
				case 'otherTaxaList':
					return this.otherTaxaList;
					break;
				default:
					return []
			}
		}
	},
};
</script>

<style lang="scss">
.ReportList {
	min-height: 6rem;
	margin-bottom: $space-lg;
}
.ReportList-heading {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	justify-content: space-between;
	@include inset-responsive;
	padding-top: 0 !important;
	padding-bottom: $space-sm !important;
	.Button--hollow {
		align-self: center;
	}
}
.ReportList-list {
	list-style: none;
	margin: 0;
	[dir='rtl'] & {
		margin: 0;
	}
	background-color: $color-neutral-1;
	display: flex;
	flex-direction: column;
	padding: 1px 0;
	gap: 1px;
	width: 100%;
	max-width: 100%;
	overflow-x: visible;
}
.ReportList-subheading {
	@include inset-squish-responsive;
	padding-bottom: 0;
	@include breakpoint(medium) {
		padding-bottom: 0;
	}
}

.ReportList-checklists {
	list-style: none;
	margin: 0;
	[dir='rtl'] & {
		margin: 0;
	}
	margin-bottom: $space-xl;
	background-color: $color-neutral-1;
	display: flex;
	flex-direction: column;
	padding-top: 1px;
	gap: 1px;
	// margin-left: var(--paddingToFill, -1rem);
	// margin-right: var(--paddingToFill, -1rem);
	// width: calc(100% + calc(var(--paddingtoFill, 1rem) * 2));
	// @include breakpoint(medium) {
	// 	margin-left: 0;
	// 	margin-right: 0;
	// 	width: 100%;
	// }
	li {
		background-color: $color-white;
		@include inset-squish-md;
		@include breakpoint(large) {
			padding-left: $space-lg;
			padding-right: $space-lg;
		}
	}
}

.print {
	.ReportList {
		min-height: auto;
	}
	.ReportList-list {
		padding: 0;
		gap: 0;
		background-color: transparent;
	}
	.ReportList-heading,
	.ReportList-subheading {
		color: $color-black;
		padding: 0;
		margin: 0;
		page-break-after: avoid;
	}
}
</style>
